import { useState, useEffect, useCallback } from 'react';

/**
 * Token storage keys
 */
const ACCESS_TOKEN_KEY = 'siteawake_access_token';
const REFRESH_TOKEN_KEY = 'siteawake_refresh_token';

/**
 * Store access token in localStorage
 */
export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

/**
 * Get access token from localStorage
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Store refresh token in localStorage
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

/**
 * Get refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store both tokens at once
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
}

/**
 * Clear all tokens from localStorage (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if user has a valid access token stored
 * Note: This only checks for token presence, not validity
 */
export function hasAccessToken(): boolean {
  const token = getAccessToken();
  return token !== null && token.length > 0;
}

/**
 * Parse JWT token payload (without verification)
 * Returns null if token is invalid or expired
 */
export function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    if (!payload) {
      return null;
    }
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Check if token is expired (with optional buffer time in seconds)
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = bufferSeconds * 1000;
  return currentTime >= expirationTime - bufferTime;
}

/**
 * Refresh the access token using the refresh token
 * Returns the new access token or null if refresh failed
 */
export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch('/api/trpc/auth.refreshToken', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const data = (await response.json()) as {
      result?: {
        data?: {
          accessToken?: string;
          refreshToken?: string;
        };
      };
    };

    const newAccessToken = data.result?.data?.accessToken;
    const newRefreshToken = data.result?.data?.refreshToken;

    if (newAccessToken) {
      setAccessToken(newAccessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      return newAccessToken;
    }

    clearTokens();
    return null;
  } catch {
    clearTokens();
    return null;
  }
}

/**
 * Get a valid access token, refreshing if necessary
 */
export async function getValidAccessToken(): Promise<string | null> {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return null;
  }

  if (!isTokenExpired(accessToken)) {
    return accessToken;
  }

  // Token is expired or about to expire, try to refresh
  return refreshAccessToken();
}

/**
 * Hook to check if user is authenticated
 * Returns loading state while checking token validity
 */
export function useIsAuthenticated(): {
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => void;
} {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const checkAuth = useCallback(() => {
    const token = getAccessToken();

    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    // Check if token is valid (not expired)
    if (!isTokenExpired(token)) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Token is expired, try to refresh
    setIsLoading(true);
    refreshAccessToken()
      .then((newToken) => {
        setIsAuthenticated(newToken !== null);
      })
      .catch(() => {
        setIsAuthenticated(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return { isAuthenticated, isLoading, checkAuth };
}

/**
 * Hook to get current user info from token
 */
export function useCurrentUser(): {
  userId: string | null;
  email: string | null;
} {
  const token = getAccessToken();

  if (!token) {
    return { userId: null, email: null };
  }

  const payload = parseJwtPayload(token);
  if (!payload) {
    return { userId: null, email: null };
  }

  return {
    userId: typeof payload.sub === 'string' ? payload.sub : null,
    email: typeof payload.email === 'string' ? payload.email : null,
  };
}
